import axios from 'axios';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { api_urls } from '../api/api-urls';
import { RootState } from '../store';
import {
    clearImpersonation,
    setImpersonationToken,
    setShowAttentionModal,
    updateTimeRemaining,
} from '../store/reducers/impersonationSlice';
import { openToast } from '../store/reducers/toastSlice';
import { updateLoginStatus, updateLoginUserDetails } from '../store/reducers/userSlice';
import { markJustLoggedIn } from '../utils/auth-session-cleanup';
import { isElloPaymentLinkContext, NavigationConfig } from '../utils/navigation-constants';

/**
 * Hook to handle super user impersonation with 60-minute session timeout
 * Parses token from URL and manages impersonation session via Redux
 */
export const useImpersonation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const sessionTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Ello email/SMS links use `?token=` for the payment JWT — never treat as super-user impersonation.
        if (isElloPaymentLinkContext(location.pathname, location.search)) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (token) {
            // Store token in sessionStorage (not localStorage - it's a temporary impersonation)
            sessionStorage.setItem('impersonationToken', token);

            // Set the token in Redux store for API authentication
            // Include minimal user details to ensure proper app state
            dispatch(
                updateLoginUserDetails({
                    user: {
                        // Minimal user object for impersonation
                        Id: null,
                        Name: 'Super User',
                        Email: null,
                    },
                    token: token,
                    refreshToken: null as string | null,
                }),
            );

            // Set login status to true
            dispatch(updateLoginStatus(true));

            // Dispatch Redux action to set impersonation token
            dispatch(setImpersonationToken(token));

            // Show attention modal
            dispatch(setShowAttentionModal(true));
        }
    }, [location.pathname, location.search, dispatch]);

    // The impersonation flag is persisted (localStorage) but the impersonation token lives in
    // sessionStorage, so it only survives within the tab that started the session. If the flag is
    // set without a matching sessionStorage token, it is stale (e.g. a previous impersonation was
    // abandoned, or the browser was restarted) and must not block a real customer's payments.
    useEffect(() => {
        if (!impersonation?.isImpersonating) {
            return;
        }
        const sessionToken = sessionStorage.getItem('impersonationToken');
        if (!sessionToken || sessionToken !== impersonation.token) {
            sessionStorage.removeItem('impersonationStartTime');
            dispatch(clearImpersonation());
        }
    }, [impersonation?.isImpersonating, impersonation?.token, dispatch]);

    // Handle impersonation session expiration — declared before startCountdown so it can be referenced
    const handleImpersonationExpired = useCallback(() => {
        // Clear timers
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }
        if (sessionTimerRef.current) {
            clearTimeout(sessionTimerRef.current);
        }

        // Call logout API
        const refreshTokenValue = sessionStorage.getItem('impersonationToken');
        if (refreshTokenValue) {
            axios({
                method: 'POST',
                url: api_urls.Logout,
                headers: {
                    Authorization: refreshTokenValue,
                },
            });
        }

        // Clear impersonation data
        sessionStorage.removeItem('impersonationToken');
        sessionStorage.removeItem('impersonationStartTime');
        dispatch(clearImpersonation());

        dispatch(
            openToast({
                show: true,
                isAlertOpen: true,
                message: 'Your Superuser Session has Expired. Redirecting to Login.',
                severity: 'info',
            }),
        );

        // Redirect to login after brief delay
        setTimeout(() => {
            dispatch(updateLoginStatus(false));
            navigate(NavigationConfig.Login);
        }, 1000);
    }, [dispatch, navigate]);

    // Start countdown timer
    const startCountdown = useCallback(() => {
        const SESSION_DURATION = 60 * 60 * 1000; // 60 minutes in milliseconds

        // Use existing start time if resuming after page reload, otherwise create new one
        const existingStartTime = sessionStorage.getItem('impersonationStartTime');
        const startTime = existingStartTime ? parseInt(existingStartTime) : Date.now();

        // Store start time in sessionStorage to persist across page reloads
        sessionStorage.setItem('impersonationStartTime', startTime.toString());

        // Clear existing interval if any
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }

        countdownIntervalRef.current = setInterval(() => {
            const storedStartTime = sessionStorage.getItem('impersonationStartTime');
            if (!storedStartTime) {
                clearInterval(countdownIntervalRef.current!);
                return;
            }

            const elapsed = Date.now() - parseInt(storedStartTime);
            const remaining = SESSION_DURATION - elapsed;

            if (remaining > 0) {
                const totalSeconds = Math.floor(remaining / 1000);
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = totalSeconds % 60;

                // Dispatch Redux action to update time remaining
                dispatch(updateTimeRemaining({ minutes, seconds }));
            } else {
                clearInterval(countdownIntervalRef.current!);
                handleImpersonationExpired();
            }
        }, 1000);

        // Set a backup timeout to ensure logout happens
        if (sessionTimerRef.current) {
            clearTimeout(sessionTimerRef.current);
        }

        sessionTimerRef.current = setTimeout(() => {
            handleImpersonationExpired();
        }, SESSION_DURATION);
    }, [dispatch, handleImpersonationExpired]);

    // Resume countdown on page reload if impersonation is active
    useEffect(() => {
        if (impersonation?.isImpersonating && !impersonation?.showAttentionModal) {
            const startTime = sessionStorage.getItem('impersonationStartTime');
            if (startTime) {
                // Resume the countdown with the stored start time
                startCountdown();
            }
        }
    }, [impersonation?.isImpersonating, impersonation?.showAttentionModal, startCountdown]);

    // Handle closing the attention modal and navigate to contracts
    const handleAttentionModalClose = () => {
        // Start the 60-minute countdown timer NOW (after user clicks OK)
        startCountdown();

        // Same post-login setup screen (contracts + dashboard prefetch) that a regular
        // login triggers via App.tsx's isLoggedIn effect — deferred until the modal closes
        // so the loader is actually visible, instead of running (invisibly) underneath it.
        markJustLoggedIn();

        // Dispatch Redux action to hide modal
        dispatch(setShowAttentionModal(false));

        // Remove token from URL for cleaner appearance (after user clicks OK)
        window.history.replaceState({}, document.title, location.pathname);

        // Give React time to process state update before navigating
        setTimeout(() => {
            // Navigate to contracts page after modal is closed
            // Use replace to prevent going back to the token URL
            navigate(NavigationConfig.ContractOverview, { replace: true });
        }, 100);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
            if (sessionTimerRef.current) {
                clearTimeout(sessionTimerRef.current);
            }
        };
    }, []);

    return {
        impersonation,
        handleAttentionModalClose,
    };
};
